import React, { useEffect, useRef, useState } from 'react';
import { fetchTripsByCategory } from '../../functions/FetchTrips'; // Add this import

const VISIBLE_CARDS = 2; // Base number of fully visible cards
const PARTIAL_CARD_VISIBILITY = 0.5; // How much of the next card to show (0 to 1)
const CARD_WIDTH = 200; // px, should match minWidth/maxWidth below
const GAP = 8; // px, should match gap below

function HeroTripCards() {
  const [startIdx, setStartIdx] = useState(0);
  const [animating, setAnimating] = useState(false);
  const intervalRef = useRef();
  const [trips, setTrips] = useState([]); // Add state for trips
  const [isHovered, setIsHovered] = useState(false); // Track hover state

  useEffect(() => {
    // Fetch trips with category "trending"
    const fetchTrendingTrips = async () => {
      const trendingTrips = await fetchTripsByCategory('trending-india' || 'trending-international'); // Use fetchTripsByCategory
      setTrips(trendingTrips || []);
    };
    fetchTrendingTrips();
  }, []);

  // Prepare a sliding window of cards, enough for visibility + animation
  const getSlidingTrips = () => {
    if (!trips.length) return [];
    const cardsNeeded = Math.min(
      Math.ceil(VISIBLE_CARDS + PARTIAL_CARD_VISIBILITY) + 1,
      trips.length
    );
    // Only unique trips, no duplicates
    const arr = [];
    for (let i = 0; i < cardsNeeded; i++) {
      const idx = (startIdx + i) % trips.length;
      if (arr.find(t => t && t.id === trips[idx].id)) continue;
      arr.push(trips[idx]);
    }
    return arr;
  };
  const slidingTrips = getSlidingTrips();

  useEffect(() => {
    if (!trips.length || isHovered) return; // Pause if hovered
    intervalRef.current = setInterval(() => {
      setAnimating(true);
      setTimeout(() => {
        setStartIdx(prev => (prev + 1) % trips.length);
        setAnimating(false);
      }, 400); // match transition duration
    }, 2500);
    return () => clearInterval(intervalRef.current);
  }, [trips, isHovered]); // Add isHovered as dependency

  // The sliding container will shift left by one card width when animating
  const slideStyle = {
    display: 'flex',
    gap: `${GAP}px`,
    transition: animating ? 'transform 0.4s cubic-bezier(.4,0,.2,1)' : 'none',
    transform: animating ? `translateX(-${CARD_WIDTH + GAP}px)` : 'translateX(0)', // <-- fixed closing quote here
    willChange: 'transform',
  };

  // Calculate the width needed for the visible + partial cards and gaps
  const containerWidth = (VISIBLE_CARDS + PARTIAL_CARD_VISIBILITY) * CARD_WIDTH + (VISIBLE_CARDS * GAP);

  return (
    <div
      style={{ overflow: 'hidden', width: `${containerWidth}px` }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div style={slideStyle}>
        {slidingTrips.map((trip, idx) => (
          trip && (
            <div
              className="desktop-trip-card position-relative"
              key={trip.id + '-' + idx} // Use unique key including idx for re-renders
              style={{
                minWidth: CARD_WIDTH,
                maxWidth: CARD_WIDTH + 30, // Allow slight flex if needed, but base on CARD_WIDTH
                flex: `0 0 ${CARD_WIDTH}px`,
                borderRadius: 6,
                background: '#fff',
                boxShadow: '0 1px 6px rgba(0,0,0,0.08)',
                overflow: 'hidden',
                position: 'relative',
                transition: 'all 0.4s cubic-bezier(.4,0,.2,1)', // Card style transition (optional)
                cursor: 'pointer', // Make card look clickable
              }}
              onClick={() => window.location.href = `/itinerary/${trip.id}`} // Make whole card clickable
            >
              <div className="trip-badge position-absolute top-0 start-0 m-1 px-1 py-0 text-white rounded" style={{ backgroundColor: '#FFA500', fontSize: 10, zIndex: 3 }}>
                {trip.badge || "Trending"}
              </div>
              {trip.discount && (
                <div className="trip-discount position-absolute top-0 end-0 m-1 px-1 py-0 bg-danger text-white rounded" style={{ fontSize: 10, zIndex: 3 }}>
                  {trip.discount}
                </div>
              )}
              <div className="trip-image position-relative" style={{ height: '250px' }}>
                <img
                  src={trip.cover_image_url || "/images/placeholder.jpg"}
                  className="img-fluid w-100"
                  alt={trip.tripname}
                  style={{ borderRadius: '6px 6px 0 0', height: '250px', objectFit: 'cover' }}
                />
                <div className="trip-overlay" style={{
                  position: 'absolute',
                  left: 0, right: 0, top: 0, bottom: 0,
                  background: 'linear-gradient(to top, rgba(0,0,0,0.55), rgba(0,0,0,0.02) 60%)',
                  borderRadius: '6px 6px 0 0',
                  zIndex: 1,
                }}></div>
                {/* Overlay trip content on image */}
                {/* Add dark overlay behind the bottom text */}
                <div
                  style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: 0,
                    height: '110px', 
                    background: 'linear-gradient(to top, rgba(0,0,0,0.78), rgba(0,0,0,0.18) 85%, transparent)',
                    zIndex: 1.5,
                    pointerEvents: 'none',
                  }}
                />
                <div
                  className="trip-content position-absolute bottom-0 start-0 w-100 px-2 pb-2"
                  style={{
                    zIndex: 2,
                    color: '#fff',
                    pointerEvents: 'none',
                  }}
                >
                  <h6 className="trip-title mb-1" style={{ fontSize: 13, textShadow: '0 1px 2px rgba(0,0,0,0.2)' }}>{trip.tripname}</h6>
                  <div className="trip-details mb-1" style={{ fontSize: 10 }}>
                    <div className="d-flex align-items-center">
                      <i className="fas fa-calendar-alt me-1"></i>
                      <small>{trip.days && trip.nights ? `${trip.days}D/${trip.nights}N` : "N/A"}</small>
                      <span className="mx-1">|</span>
                      <i className="fas fa-map-marker-alt me-1"></i>
                      <small>{trip.location || "N/A"}</small>
                    </div>
                  </div>
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="price fw-bold" style={{ fontSize: 12, textShadow: '0 1px 2px rgba(0,0,0,0.2)' }}>₹ {trip.display_price || "N/A"}</div>
                    <button
                      className="btn btn-xs btn-outline-light py-0 px-2"
                      style={{ fontSize: 10, lineHeight: 1.2, pointerEvents: 'auto' }}
                      onClick={e => {
                        e.stopPropagation();
                        window.location.href = `/itinerary/${trip.id}`;
                      }}
                    >
                      View
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )
        ))}
      </div>
    </div>
  );
}

export default HeroTripCards;
